import { Link } from "@tanstack/react-router"
import { Archive, ArchiveRestore, Plus } from "lucide-react"
import { useState } from "react"
import { H1 } from "@holigral/shared/ui/components"
import { Badge } from "@holigral/shared/ui/components/ui/badge"
import { Button } from "@holigral/shared/ui/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@holigral/shared/ui/components/ui/table"
import {
  CheckboxField,
  IconButton,
  Tooltip,
} from "@holigral/shared/ui/composed"
import BriefCreateDialog from "./BriefCreateDialog"
import useToggleArchived from "./useToggleArchived"
import { useGetBriefsQuery } from "./api"
import { useT } from "@/i18n"
import { useCurrentTenant } from "@/tenant"
import { formatDate } from "@/utils"

type Props = {
  readonly includeArchived: boolean
  readonly onIncludeArchivedChange: (value: boolean) => void
}

export default function BriefsPage({
  includeArchived,
  onIncludeArchivedChange,
}: Props) {
  const { t } = useT("brief")
  const { tenantSlug } = useCurrentTenant()
  const [createOpen, setCreateOpen] = useState(false)
  const { data: briefs, isLoading } = useGetBriefsQuery(includeArchived)
  const toggleArchived = useToggleArchived()

  const rows = briefs ?? []

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <H1>{t("title")}</H1>
        <div className="flex items-center gap-4">
          <CheckboxField
            name="includeArchived"
            label={t("includeArchived")}
            checked={includeArchived}
            onCheckedChange={onIncludeArchivedChange}
          />
          <Button
            onClick={() => {
              setCreateOpen(true)
            }}
          >
            <Plus />
            {t("new")}
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("table.name")}</TableHead>
            <TableHead>{t("table.created")}</TableHead>
            <TableHead>
              <span className="sr-only">{t("table.actions")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((brief) => (
            <TableRow key={brief.id}>
              <TableCell>
                <span className="inline-flex items-center gap-2">
                  <Link
                    to="/t/$tenantSlug/briefs/$briefId"
                    params={{ tenantSlug, briefId: brief.id }}
                    className="underline underline-offset-3 hover:text-foreground"
                  >
                    {brief.name}
                  </Link>
                  {brief.archived && (
                    <Badge variant="ghost" className="text-muted-foreground">
                      {t("archivedBadge")}
                    </Badge>
                  )}
                </span>
              </TableCell>
              <TableCell>{formatDate("date", brief.createdAt)}</TableCell>
              <TableCell className="text-right">
                <Tooltip
                  tooltip={brief.archived ? t("unarchive") : t("archive")}
                >
                  <IconButton
                    size="icon-sm"
                    icon={brief.archived ? <ArchiveRestore /> : <Archive />}
                    aria-label={brief.archived ? t("unarchive") : t("archive")}
                    onClick={() => toggleArchived(brief)}
                  />
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && !isLoading && (
            <TableRow>
              <TableCell
                colSpan={3}
                className="py-8 text-center text-muted-foreground"
              >
                {t("table.empty")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <BriefCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  )
}
